import type { KolorWypowiedzi } from "@shared/koloryWypowiedzi.js";
import type {
  KorektaWskazania,
  PostepRozmowy,
  RecenzjaRozmowa,
  RecenzjaWskazanie,
  StanRozmowyRecenzenta,
  Werdykt,
  ZapisKorektyWskazania,
} from "@shared/recenzjeTypy.js";
import { zakresWskazania, type ZakresCytatu } from "../components/transcript/fragmenty.js";
import type { MigawkaZapisu } from "./autozapis.js";
import { ApiError } from "./api.js";

/**
 * Czyste funkcje widoku recenzenta: napisy, postep, ukladanie wskazan przy wypowiedziach, szkice pol, bledy.
 * Zero DOM i zero sieci, zeby dalo sie je testowac wprost.
 */

// ---------------------------------------------------------------------------
// Trasa
// ---------------------------------------------------------------------------

/**
 * Czy adres nalezy do widoku recenzenta (/r/<token>...). Decyduje main.tsx PRZED wyborem aplikacji, wiec widok
 * recenzenta nigdy nie przechodzi przez bramke logowania. Dokladnie prefiks "/r/": "/rozmowy" ani "/r" tu nie wpadaja.
 */
export function czyTrasaRecenzenta(sciezka: string): boolean {
  return sciezka.startsWith("/r/");
}

// ---------------------------------------------------------------------------
// Postep i stan rozmowy
// ---------------------------------------------------------------------------

export const ETYKIETY_STANU: Readonly<Record<StanRozmowyRecenzenta, string>> = {
  NIE_ZACZETA: "nie zaczęta",
  W_TOKU: "w toku",
  PRZEJRZANA: "przejrzana",
};

/** "1 z 2 wskazań", "0 z 1 wskazania". Po "z" rzeczownik stoi w dopelniaczu: liczba pojedyncza tylko przy jedynce. */
export function opisPostepu(postep: Pick<PostepRozmowy, "ocenionych" | "wskazan">): string {
  if (postep.wskazan <= 0) return "brak wskazań modelu";
  return `${postep.ocenionych} z ${postep.wskazan} ${postep.wskazan === 1 ? "wskazania" : "wskazań"}`;
}

/** Napis w pasku postepu rozmowy: "Oceniono 1 z 2 wskazań". */
export function napisPostepuRozmowy(postep: Pick<PostepRozmowy, "ocenionych" | "wskazan">): string {
  if (postep.wskazan <= 0) return "Model nie zgłosił wskazań w tej rozmowie";
  return `Oceniono ${opisPostepu(postep)}`;
}

/** Szerokosc wypelnienia paska w procentach (calkowita, 0-100). Brak wskazan to 0, nie dzielenie przez zero. */
export function procentPostepu(postep: Pick<PostepRozmowy, "ocenionych" | "wskazan">): number {
  if (postep.wskazan <= 0) return 0;
  const procent = Math.round((100 * postep.ocenionych) / postep.wskazan);
  return Math.max(0, Math.min(100, procent));
}

// ---------------------------------------------------------------------------
// Godzina zapisu i napis statusu
// ---------------------------------------------------------------------------

/** "14:05": godzina i minuta czasu lokalnego, zawsze dwie cyfry (bez zaleznosci od ustawien przegladarki). */
export function czasZapisu(data: Date): string {
  const dwie = (n: number): string => String(n).padStart(2, "0");
  return `${dwie(data.getHours())}:${dwie(data.getMinutes())}`;
}

/** Napis przy pasku postepu. Blad ma osobny przycisk "Spróbuj ponownie", wiec napis konczy sie na pierwszym zdaniu. */
export function napisStatusuZapisu(migawka: Pick<MigawkaZapisu, "stan" | "zapisanoO">): string {
  switch (migawka.stan) {
    case "saving":
      return "Zapisuję...";
    case "saved":
      return migawka.zapisanoO ? `Zapisano ${czasZapisu(migawka.zapisanoO)}` : "Zapisano";
    case "error":
      return "Nie udało się zapisać.";
    case "idle":
      return "Zmiany zapisują się same";
  }
}

// ---------------------------------------------------------------------------
// Szkice pol przy wskazaniu
// ---------------------------------------------------------------------------

/** To, co recenzent ma w polach przy jednym wskazaniu. Teksty sa zawsze napisami (pusty = brak). */
export interface KorektaSzkic {
  werdykt: Werdykt | null;
  komentarz: string;
  wariant: string;
}

export function szkicZKorekty(korekta: KorektaWskazania | null): KorektaSzkic {
  return {
    werdykt: korekta?.werdykt ?? null,
    komentarz: korekta?.komentarz ?? "",
    wariant: korekta?.wariant ?? "",
  };
}

/** Sam tekst z bialych znakow to brak tekstu: serwer dostaje null, nie spacje. */
function tekstAlboNull(tekst: string): string | null {
  return tekst.trim().length === 0 ? null : tekst;
}

export function szkicDoZapisu(klucz: string, szkic: KorektaSzkic): ZapisKorektyWskazania {
  return {
    klucz,
    werdykt: szkic.werdykt,
    komentarz: tekstAlboNull(szkic.komentarz),
    wariant: tekstAlboNull(szkic.wariant),
  };
}

/** Czy pola komentarza i wlasnej wersji maja byc widoczne: przy niezgodzie, a takze gdy cos w nich juz stoi. */
export function czyPokazacPolaTekstowe(szkic: KorektaSzkic): boolean {
  return (
    szkic.werdykt === "CZESCIOWO" ||
    szkic.werdykt === "NIE_ZGADZAM_SIE" ||
    szkic.komentarz.trim().length > 0 ||
    szkic.wariant.trim().length > 0
  );
}

/** Podpowiedz pod przelacznikiem werdyktu: co oznacza obecny wybor (albo brak wyboru). */
export function podpowiedzWerdyktu(werdykt: Werdykt | null, rodzaj: RecenzjaWskazanie["rodzaj"]): string {
  switch (werdykt) {
    case null:
      return "Bez kliknięcia zakładamy zgodę.";
    case "ZGADZAM_SIE":
      return "Zgadzasz się z tym wskazaniem. Kliknij jeszcze raz, żeby cofnąć wybór.";
    case "CZESCIOWO":
      return "Zgadzasz się częściowo. Dopisz, w czym.";
    case "NIE_ZGADZAM_SIE":
      return rodzaj === "do-poprawy"
        ? "Nie zgadzasz się. Dopisz komentarz i swoją wersję."
        : "Nie zgadzasz się. Dopisz komentarz.";
  }
}

// ---------------------------------------------------------------------------
// Nazwy grup werdyktu (A-5)
// ---------------------------------------------------------------------------

/** Pierwsze slowa cytatu do nazwy grupy: kilka slow, bez urywania w pol slowa. Pusty cytat to null. */
export function poczatekCytatu(cytat: string | null, maxSlow = 5, maxZnakow = 40): string | null {
  const slowa = (cytat ?? "").trim().split(/\s+/).filter((s) => s !== "");
  if (slowa.length === 0) return null;
  let wynik = "";
  let uzyte = 0;
  for (const slowo of slowa.slice(0, maxSlow)) {
    const kandydat = wynik === "" ? slowo : `${wynik} ${slowo}`;
    if (wynik !== "" && kandydat.length > maxZnakow) break;
    wynik = kandydat;
    uzyte += 1;
  }
  // Pojedyncze, bardzo dlugie slowo tez ucinamy, zeby nazwa grupy nie rosla bez konca.
  if (wynik.length > maxZnakow) wynik = wynik.slice(0, maxZnakow);
  return uzyte < slowa.length || wynik.length < slowa.join(" ").length ? `${wynik}...` : wynik;
}

type DoNazwyGrupy = Pick<RecenzjaWskazanie, "parametr" | "numerWypowiedzi" | "rodzaj" | "cytat">;

/**
 * Nazwa grupy werdyktu dla czytnika ekranu: parametr, wypowiedz, RODZAJ (mocna strona albo do poprawy) i poczatek cytatu.
 * Dwa wskazania tego samego parametru przy jednej wypowiedzi (mocna strona i rzecz do poprawy albo dwa cytaty) musza sie
 * nazywac inaczej, bo inaczej recenzent nie wie, ktorej grupie ustawia werdykt.
 */
export function opisGrupyWskazania(w: DoNazwyGrupy): string {
  const czesci: string[] = [w.parametr];
  if (w.numerWypowiedzi !== null) czesci.push(`wypowiedź ${w.numerWypowiedzi}`);
  czesci.push(w.rodzaj === "mocna-strona" ? "mocna strona" : "do poprawy");
  const poczatek = poczatekCytatu(w.cytat);
  if (poczatek) czesci.push(`cytat: „${poczatek}”`);
  return `Twoja ocena wskazania: ${czesci.join(", ")}`;
}

/**
 * Nazwy grup dla wszystkich wskazan rozmowy (klucz -> nazwa). Gdyby mimo rodzaju i cytatu dwie nazwy byly identyczne
 * (np. dwa wskazania bez cytatu), dostaja numer "1 z 2", "2 z 2": nazwy sa unikalne z konstrukcji.
 */
export function opisyGrupWskazan(wskazania: readonly (DoNazwyGrupy & Pick<RecenzjaWskazanie, "klucz">)[]): Map<string, string> {
  const bazowe = wskazania.map((w) => opisGrupyWskazania(w));
  const ile = new Map<string, number>();
  for (const nazwa of bazowe) ile.set(nazwa, (ile.get(nazwa) ?? 0) + 1);

  const ktory = new Map<string, number>();
  const wynik = new Map<string, string>();
  wskazania.forEach((w, i) => {
    const nazwa = bazowe[i];
    const razem = ile.get(nazwa) ?? 1;
    const nr = (ktory.get(nazwa) ?? 0) + 1;
    ktory.set(nazwa, nr);
    wynik.set(w.klucz, razem > 1 ? `${nazwa}, ${nr} z ${razem}` : nazwa);
  });
  return wynik;
}

// ---------------------------------------------------------------------------
// Wskazania przy wypowiedziach
// ---------------------------------------------------------------------------

export interface WskazaniaWgWypowiedzi {
  /** Numer wypowiedzi (od 1) -> jej wskazania w kolejnosci z serwera. */
  wgNumeru: Map<number, RecenzjaWskazanie[]>;
  /** Wskazania bez wypowiedzi w transkrypcie: numer null albo numer spoza rozmowy. */
  bezWypowiedzi: RecenzjaWskazanie[];
}

export function wskazaniaWgWypowiedzi(rozmowa: Pick<RecenzjaRozmowa, "wypowiedzi" | "wskazania">): WskazaniaWgWypowiedzi {
  const istniejace = new Set(rozmowa.wypowiedzi.map((w) => w.numer));
  const wgNumeru = new Map<number, RecenzjaWskazanie[]>();
  const bezWypowiedzi: RecenzjaWskazanie[] = [];

  for (const wskazanie of rozmowa.wskazania) {
    const numer = wskazanie.numerWypowiedzi;
    if (numer === null || !istniejace.has(numer)) {
      bezWypowiedzi.push(wskazanie);
      continue;
    }
    const lista = wgNumeru.get(numer);
    if (lista) lista.push(wskazanie);
    else wgNumeru.set(numer, [wskazanie]);
  }
  return { wgNumeru, bezWypowiedzi };
}

/**
 * Zakresy podkreslen po numerze wypowiedzi. Podkreslenie powstaje WYLACZNIE przy cytacie zweryfikowanym w kodzie
 * (cytatZweryfikowany) i o zakresie, ktory miesci sie w tekscie: tej decyzji pilnuje `zakresWskazania`, to samo miejsce,
 * z ktorego korzysta transkrypt w widoku administratora.
 */
export function zakresyCytatow(rozmowa: Pick<RecenzjaRozmowa, "wypowiedzi" | "wskazania">): Map<number, ZakresCytatu[]> {
  const tekstyPoNumerze = new Map(rozmowa.wypowiedzi.map((w) => [w.numer, w.tekst] as const));
  const wynik = new Map<number, ZakresCytatu[]>();

  for (const wskazanie of rozmowa.wskazania) {
    const numer = wskazanie.numerWypowiedzi;
    if (numer === null) continue;
    const tekst = tekstyPoNumerze.get(numer);
    if (tekst === undefined) continue;

    const zakres = zakresWskazania(
      { quoteVerified: wskazanie.cytatZweryfikowany, quoteStart: wskazanie.cytatStart, quoteEnd: wskazanie.cytatEnd },
      tekst.length,
    );
    if (!zakres) continue;

    const lista = wynik.get(numer) ?? [];
    lista.push({ id: wskazanie.klucz, rodzaj: wskazanie.rodzaj, start: zakres.start, koniec: zakres.koniec });
    wynik.set(numer, lista);
  }
  return wynik;
}

/** Kolor dymka z kontraktu na wartosc atrybutu `data-jakosc` ("brak" = bez atrybutu, a nie ocena "srednio"). */
export function jakoscDymka(kolor: KolorWypowiedzi): "najlepsze" | "srednie" | "najslabsze" | undefined {
  return kolor === "brak" ? undefined : kolor;
}

/** Ile wskazan stoi przy wypowiedziach od pozycji `od` (indeks w tablicy wypowiedzi) do konca. */
export function ileWskazanOdPozycji(
  wypowiedzi: readonly { numer: number }[],
  wgNumeru: ReadonlyMap<number, readonly unknown[]>,
  od: number,
): number {
  let suma = 0;
  for (let i = Math.max(0, od); i < wypowiedzi.length; i++) suma += wgNumeru.get(wypowiedzi[i].numer)?.length ?? 0;
  return suma;
}

// ---------------------------------------------------------------------------
// Bledy jako zdania
// ---------------------------------------------------------------------------

export type RodzajBledu =
  /** Link przestal dzialac (wygasl, uniewazniony): nic wiecej sie nie zapisze. */
  | "link"
  | "rozmowa"
  /** Ocena rozmowy zmienila sie od wczytania: klucze wskazan sie rozjechaly. */
  | "ocena"
  | "nieoceniona"
  | "dane"
  | "siec"
  | "serwer";

export function rodzajBledu(blad: unknown): RodzajBledu {
  if (!(blad instanceof ApiError)) return "siec";
  switch (blad.code) {
    case "link_nieprawidlowy":
      return "link";
    case "nie_ma_takiej_rozmowy":
      return "rozmowa";
    case "nie_ma_takiego_wskazania":
      return "ocena";
    case "rozmowa_nieoceniona":
      return "nieoceniona";
    case "bledne_dane":
      return "dane";
    default:
      return "serwer";
  }
}

/**
 * 404 `nie_ma_takiego_wskazania` dla UWAGI recenzenta (PUT albo DELETE .../pominiete/:id): uwagi nie ma po stronie serwera,
 * bo usunieto ja w innej karcie albo na innym urzadzeniu. To nie jest "ocena sie zmienila" (to dotyczy wskazan modelu),
 * wiec kod uwag obsluguje ten blad sam, zanim trafi do `rodzajBledu`: usuniecie liczy go za powodzenie, zmiana tworzy
 * uwage od nowa.
 */
export function czyBrakUwagiNaSerwerze(blad: unknown): boolean {
  return blad instanceof ApiError && blad.status === 404 && blad.code === "nie_ma_takiego_wskazania";
}

/**
 * Baner przy nieudanym ZAPISIE z 409 rozmowa_nieoceniona: przy rozmowie, ktora recenzent juz przeglada, oznacza to, ze
 * administrator wlasnie przelicza ocene ("Ta rozmowa nie ma jeszcze gotowej oceny" brzmialoby jak blad rozmowy).
 */
export const TEKST_OCENA_PRZELICZANA = "Ocena jest właśnie przeliczana. Spróbuj za chwilę, Twoje zmiany zostają na tej stronie.";

export function tekstBledu(blad: unknown): string {
  switch (rodzajBledu(blad)) {
    case "link":
      return "Ten link przestał działać: jest nieprawidłowy albo wygasł. Poproś o nowy.";
    case "rozmowa":
      return "Tej rozmowy nie ma pod tym linkiem.";
    case "ocena":
      return "Ocena tej rozmowy zmieniła się od wczytania strony. Wczytaj stronę ponownie, żeby zapisywać uwagi. Tekst, którego nie chcesz stracić, skopiuj wcześniej.";
    case "nieoceniona":
      return "Ta rozmowa nie ma jeszcze gotowej oceny.";
    case "dane":
      return "Serwer odrzucił te dane. Sprawdź, czy tekst nie jest za długi.";
    case "serwer":
      return "Serwer nie odpowiada. Spróbuj ponownie za chwilę.";
    case "siec":
      return "Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.";
  }
}
